/**
 * Ордерблоки.
 *
 * Последняя свеча против хода перед сильным рывком — место, откуда
 * движение началось и где, по логике метода, крупный участник набирал
 * позицию. Когда цена возвращается туда, его заявки часто снова держат.
 *
 * На графике — тем же языком, что и «Имбалансы», но отличимо с первого
 * взгляда: свои цвета (синий и оранжевый вместо зелёного и красного),
 * плотная кромка слева у свечи блока и точки касаний на краю, с которого
 * цена входит. Нетронутый блок сильнее всего — и это видно по подписи.
 *
 * Признак — тот же, что у условия «Ордер-блок» в конструкторе:
 * `orderBlockZones` из общих правил.
 */

import { orderBlockZones, type OrderBlockZone } from '@specter/rules';
import { timeframeMs, type Timeframe } from '@specter/types';
import type { Candle } from '../state/store.js';
import type { IndicatorShape, ReadoutLegendItem, ReadoutRow } from './overlays.js';
import {
  boolParam,
  colorParam,
  numberParam,
  type IndicatorContext,
  type IndicatorDefinition,
  type IndicatorParams,
} from './types.js';
import { TABLE_PARAM, percentWords, priceWords, situationRows, type Situation } from './explain.js';
import { FRAMES, LABELS, deepOf, freshOf } from './imbalances.js';

const BULL = '#3BA7FF';
const BEAR = '#FF8A3D';

interface FramedBlock extends OrderBlockZone {
  readonly frame: string;
  readonly weight: 1 | 2 | 3;
  /**
   * Свеча, которая ещё набирается, сейчас за зоной.
   *
   * Пробой — это закрытие за зоной, а не касание ценой. Пока свеча
   * блока не закрылась, блок не погашен, но и «держит» о нём сказать
   * нельзя: человек видит цену под зоной и должен видеть, что это
   * замечено.
   */
  readonly breaking?: boolean;
}

interface Found {
  readonly alive: readonly FramedBlock[];
  readonly broken: readonly FramedBlock[];
  readonly loading: readonly string[];
}

/**
 * Старший блок — по свечам графика.
 *
 * Старший ряд знает касание и пробой с точностью до своей свечи — четырёх
 * часов. Точка касания, поставленная на начало такой свечи, висела бы
 * там, где цены ещё не было. Точное место ищется по свечам графика внутри
 * старшей свечи; не нашлось (история короче) — остаётся старшее.
 */
function onChart(block: OrderBlockZone, chart: readonly Candle[], step: number): OrderBlockZone {
  const within = (from: number, test: (candle: Candle) => boolean): number => {
    for (const candle of chart) {
      if (candle.time < from) continue;
      if (candle.time >= from + step) break;
      if (test(candle)) return candle.time;
    }
    return from;
  };
  const enters = (candle: Candle): boolean => (block.side === 'up' ? candle.low <= block.upper : candle.high >= block.lower);
  const breaks = (candle: Candle): boolean => (block.side === 'up' ? candle.close < block.lower : candle.close > block.upper);
  return {
    ...block,
    touches: block.touches.map((time) => within(time, enters)),
    brokenAt: block.brokenAt === null ? null : within(block.brokenAt, breaks),
  };
}

let memo: { key: string; chart: readonly Candle[]; sources: readonly unknown[]; found: Found } | null = null;

function find(chart: readonly Candle[], p: IndicatorParams, context: IndicatorContext): Found {
  const strength = numberParam(p, 'strength', 1.5);
  const maxAlive = numberParam(p, 'maxAlive', 6);
  const maxBroken = boolParam(p, 'showBroken', true) ? numberParam(p, 'maxBroken', 3) : 0;
  const chartMs = timeframeMs(context.timeframe);

  const wanted: Array<{ frame: string; weight: 1 | 2 | 3; bars: readonly Candle[] | null; live: boolean; step: number }> = [];
  if (boolParam(p, 'tfCurrent', true)) {
    wanted.push({
      frame: LABELS[context.timeframe] ?? context.timeframe,
      weight: 1,
      bars: deepOf(chart, context),
      live: context.livePartial,
      step: chartMs,
    });
  }
  for (const [index, entry] of FRAMES.entries()) {
    if (!boolParam(p, entry.key, entry.timeframe === '4H')) continue;
    // Отмеченный таймфрейм, равный графику, — это свой (на дневном графике «1д» значит «покажи дневные»).
    if (entry.timeframe === context.timeframe) {
      if (!boolParam(p, 'tfCurrent', true)) wanted.push({ frame: entry.label, weight: 1, bars: deepOf(chart, context), live: context.livePartial, step: chartMs });
      continue;
    }
    const step = timeframeMs(entry.timeframe);
    if (step <= chartMs) continue;
    wanted.push({
      frame: entry.label,
      weight: index >= 2 ? 3 : 2,
      // Дотянут свечами графика: загруженный ряд не знает свечей, пришедших после.
      bars: freshOf(context.series(entry.timeframe), chart, entry.timeframe),
      live: true,
      step,
    });
  }

  const key = `${strength}|${maxAlive}|${maxBroken}|${context.timeframe}|${wanted.map((entry) => entry.frame).join(',')}`;
  const sources = wanted.map((entry) => entry.bars);
  if (
    memo &&
    memo.key === key &&
    memo.chart === chart &&
    memo.sources.length === sources.length &&
    memo.sources.every((source, index) => source === sources[index])
  ) {
    return memo.found;
  }

  const floor = chart[0]?.time ?? 0;
  const alive: FramedBlock[] = [];
  const broken: FramedBlock[] = [];
  const loading: string[] = [];

  for (const entry of wanted) {
    const bars = entry.bars;
    if (!bars || bars.length < 3) {
      if (entry.weight > 1) loading.push(entry.frame);
      continue;
    }
    // Импульс, который рисует ещё не закрытая свеча, может не состояться.
    const lastTime = bars[bars.length - 1]!.time;
    const blocks = orderBlockZones(bars, strength)
      .filter((block) => !(entry.live && block.formedAt === lastTime))
      // «Пробит» ещё не закрытой свечой — значит пробивается: закрытия нет.
      .map((block) =>
        entry.live && block.brokenAt === lastTime ? { ...block, brokenAt: null, breaking: true } : block,
      );
    const place = (block: OrderBlockZone & { breaking?: boolean }): FramedBlock => ({
      ...(entry.weight > 1 ? onChart(block, chart, entry.step) : block),
      frame: entry.frame,
      weight: entry.weight,
      ...(block.breaking ? { breaking: true } : {}),
    });

    alive.push(...blocks.filter((block) => block.brokenAt === null).slice(-maxAlive).map(place));
    if (maxBroken > 0) {
      broken.push(
        ...blocks
          .filter((block) => block.brokenAt !== null && block.brokenAt >= floor)
          .slice(-maxBroken)
          .map(place),
      );
    }
  }

  const found = { alive, broken, loading };
  memo = { key, chart, sources, found };
  return found;
}

function touchWords(count: number): string {
  if (count === 0) return 'не тронут';
  const tail = count % 10 === 1 && count % 100 !== 11 ? 'касание' : count % 10 >= 2 && count % 10 <= 4 && (count % 100 < 12 || count % 100 > 14) ? 'касания' : 'касаний';
  return `${count} ${tail}`;
}

function labelOf(block: FramedBlock): string {
  const arrow = block.side === 'up' ? '▲' : '▼';
  if (block.breaking) return `${block.frame} ордерблок ${arrow} · пробивается`;
  return `${block.frame} ордерблок ${arrow} · ${touchWords(block.touches.length)}`;
}

function shapesOf(chart: readonly Candle[], p: IndicatorParams, context: IndicatorContext): IndicatorShape[] {
  if (chart.length === 0) return [];
  const { alive, broken } = find(chart, p, context);
  const floor = chart[0]!.time;
  const bull = colorParam(p, 'bullColor', BULL);
  const bear = colorParam(p, 'bearColor', BEAR);
  const midline = boolParam(p, 'midline', true);
  const labels = boolParam(p, 'labels', true);
  const touches = boolParam(p, 'touches', true);
  const transparency = numberParam(p, 'opacity', 50);

  const shape = (block: FramedBlock): IndicatorShape => ({
    kind: 'zone',
    from: Math.max(block.start, floor),
    to: block.brokenAt,
    upper: block.upper,
    lower: block.lower,
    tone: block.side === 'up' ? 'up' : 'down',
    color: block.side === 'up' ? bull : bear,
    midline,
    filled: 0,
    weight: block.weight,
    closed: block.brokenAt !== null,
    transparency,
    accent: true,
    ...(touches ? { touches: block.touches.filter((time) => time >= floor) } : {}),
    // Пробитый подписи не получает: он погашен, а подписи «пробит» по всему
    // графику спорили бы с подписями действующих блоков.
    ...(labels && block.brokenAt === null ? { label: labelOf(block) } : {}),
  });

  // Пробитые снизу, над ними старшие, свои сверху — и подписи в том же порядке.
  const byWeight = [...alive].sort((a, b) => b.weight - a.weight);
  return [...broken.map(shape), ...byWeight.map(shape)];
}

function readoutOf(chart: readonly Candle[], p: IndicatorParams, context: IndicatorContext): ReadoutRow[] {
  const last = chart.at(-1);
  if (!last) return [];
  const price = last.close;
  const { alive, loading } = find(chart, p, context);

  const inside = alive
    .filter((block) => block.lower <= price && price <= block.upper)
    .sort((a, b) => b.weight - a.weight)[0];
  // Пробивающийся блок уже за ценой: ни опорой снизу, ни стеной сверху его не назвать.
  const standing = alive.filter((block) => !block.breaking);
  const above = standing.filter((block) => block.lower > price).sort((a, b) => a.lower - b.lower)[0];
  const below = standing.filter((block) => block.upper < price).sort((a, b) => b.upper - a.upper)[0];
  const upDistance = above ? ((above.lower - price) / price) * 100 : null;
  const downDistance = below ? ((price - below.upper) / price) * 100 : null;

  /*
   * Пробивается — важнее всего остального: цена уже за блоком, и
   * человек спрашивает ровно об этом — пробит он или нет.
   */
  const breaking = alive.filter((block) => block.breaking).sort((a, b) => b.weight - a.weight)[0];

  let situation: Situation;
  if (breaking) {
    situation = {
      title: `${breaking.frame} ордерблок пробивается`,
      tone: 'warn',
      text:
        breaking.side === 'up'
          ? `Свеча ${breaking.frame} сейчас ниже бычьего ордерблока ${priceWords(breaking.lower)} – ${priceWords(breaking.upper)}, но ещё не закрылась. Закроется ниже — блок пробит и погаснет. Вернётся в зону до закрытия — блок устоял.`
          : `Свеча ${breaking.frame} сейчас выше медвежьего ордерблока ${priceWords(breaking.lower)} – ${priceWords(breaking.upper)}, но ещё не закрылась. Закроется выше — блок пробит и погаснет. Вернётся в зону до закрытия — блок устоял.`,
    };
  } else if (inside) {
    const fresh = inside.touches.length <= 1;
    situation = {
      title: `В ${inside.frame} ордерблоке`,
      tone: inside.side === 'up' ? 'bull' : 'bear',
      text:
        inside.side === 'up'
          ? `Цена вернулась в ${inside.frame} бычий ордерблок — туда, откуда начинался рывок вверх. Здесь часто снова ждут покупатели${fresh ? ', а первое возвращение — самое сильное' : ''}. Закрытие свечи ниже зоны — пробой: зона больше не держит.`
          : `Цена вернулась в ${inside.frame} медвежий ордерблок — туда, откуда начинался рывок вниз. Здесь часто снова ждут продавцы${fresh ? ', а первое возвращение — самое сильное' : ''}. Закрытие свечи выше зоны — пробой: зона больше не держит.`,
    };
  } else if (upDistance === null && downDistance === null) {
    situation = {
      title: 'Рядом пусто',
      tone: 'muted',
      text: 'Действующих ордерблоков нет — все пробиты или ещё не образовались. Опереться цене не на что.',
    };
  } else if (downDistance === null || (upDistance !== null && upDistance < downDistance)) {
    situation = {
      title: 'Ближе сверху',
      tone: 'bear',
      text: `Ближайший ордерблок — ${above!.frame} ${above!.side === 'up' ? 'бычий' : 'медвежий'}, сверху, в ${percentWords(upDistance!).replace('+', '')} от цены, ${touchWords(above!.touches.length)}. Там цену может встретить продавец.`,
    };
  } else {
    situation = {
      title: 'Ближе снизу',
      tone: 'bull',
      text: `Ближайший ордерблок — ${below!.frame} ${below!.side === 'up' ? 'бычий' : 'медвежий'}, снизу, в ${percentWords(downDistance).replace('+', '')} от цены, ${touchWords(below!.touches.length)}. Там цену может подхватить покупатель.`,
    };
  }

  const span = (block: FramedBlock): string => `${priceWords(block.lower)} – ${priceWords(block.upper)}`;
  const rows: ReadoutRow[] = [...situationRows(situation)];
  if (above && upDistance !== null) {
    rows.push({ label: 'Сверху', value: `${above.frame} · ${span(above)} · ${percentWords(upDistance, 2)}`, tone: 'down' });
  }
  if (below && downDistance !== null) {
    rows.push({ label: 'Снизу', value: `${below.frame} · ${span(below)} · ${percentWords(-downDistance, 2)}`, tone: 'up' });
  }
  const bulls = alive.filter((block) => block.side === 'up').length;
  const fresh = alive.filter((block) => block.touches.length === 0).length;
  rows.push({ label: 'Действующих', value: `${bulls} бычьих · ${alive.length - bulls} медвежьих`, tone: 'muted' });
  rows.push({ label: 'Не тронутых', value: String(fresh), tone: 'muted' });
  if (loading.length > 0) rows.push({ label: 'Старшие', value: `${loading.join(', ')} грузятся…`, tone: 'muted' });
  return rows;
}

const LEGEND: readonly ReadoutLegendItem[] = [
  {
    sample: 'solid',
    term: 'Полоса',
    text: 'последняя свеча против хода перед рывком. Синяя — перед рывком вверх, держит снизу; оранжевая — перед рывком вниз, давит сверху.',
  },
  { sample: 'strong', term: 'Кромка слева', text: 'свеча, с которой зона началась. Толще — со старшего таймфрейма.' },
  { sample: 'soft', term: 'Точки', text: 'касания — каждое возвращение цены в зону. Нетронутый блок сильнее всего.' },
  { sample: 'dashed', term: 'Пунктир', text: 'середина зоны.' },
  { sample: 'faded', term: 'Погасшая', text: 'пробитый блок: свеча закрылась за ним, и он больше не держит.' },
  {
    sample: 'soft',
    term: '«Пробивается»',
    text: 'цена за блоком, но свеча его таймфрейма ещё не закрылась. Закроется за зоной — блок погаснет.',
  },
];

export const ORDER_BLOCKS: IndicatorDefinition = {
  id: 'order-blocks',
  name: 'Ордерблоки',
  description:
    'Свечи, с которых начинались сильные рывки, — со своего таймфрейма и со старших. ' +
    'Точки — касания, подпись — откуда блок и сколько раз к нему возвращались.',
  author: 'specter',
  placement: 'overlay',
  params: [
    {
      key: 'tfCurrent',
      label: 'Ордерблоки своего таймфрейма',
      hint: 'Блоки на свечах открытого графика. Выключите, если нужны только старшие.',
      type: 'boolean',
      default: true,
    },
    {
      key: 'tf15m',
      label: 'Ордерблоки 15м',
      hint: 'Показывать блоки пятнадцатиминутного графика. Видны только на графиках младше 15м.',
      type: 'boolean',
      default: false,
    },
    {
      key: 'tf1H',
      label: 'Ордерблоки 1ч',
      hint: 'Показывать блоки часового графика. Видны на графиках младше часа.',
      type: 'boolean',
      default: false,
    },
    {
      key: 'tf4H',
      label: 'Ордерблоки 4ч',
      hint: 'Показывать блоки четырёхчасового графика. Видны на графиках младше 4ч — толще кромка, весомее зона.',
      type: 'boolean',
      default: true,
    },
    {
      key: 'tf1D',
      label: 'Ордерблоки 1д',
      hint: 'Показывать блоки дневного графика. Самые весомые: от них отталкиваются неделями.',
      type: 'boolean',
      default: false,
    },
    {
      key: 'strength',
      label: 'Сила рывка, во сколько раз больше обычной свечи',
      hint: 'Блок засчитывается, только если за ним был рывок сильнее обычной свечи во столько раз. Выше — меньше блоков, но за каждым настоящее движение.',
      type: 'number',
      default: 1.5,
      min: 1,
      max: 5,
      step: 0.1,
    },
    {
      key: 'maxAlive',
      label: 'Действующих на таймфрейм',
      hint: 'Сколько последних непробитых блоков держать с каждого таймфрейма. Больше — полнее картина, но теснее на графике.',
      type: 'number',
      default: 6,
      min: 1,
      max: 30,
      step: 1,
    },
    {
      key: 'showBroken',
      label: 'Показывать пробитые',
      hint: 'Блоки, за которыми свеча уже закрылась: гаснут и обрываются на пробое. Видно, какие зоны рынок сломал.',
      type: 'boolean',
      default: true,
    },
    {
      key: 'maxBroken',
      label: 'Пробитых на таймфрейм',
      hint: 'Сколько последних пробитых блоков показывать с каждого таймфрейма.',
      type: 'number',
      default: 3,
      min: 1,
      max: 30,
      step: 1,
    },
    {
      key: 'touches',
      label: 'Точки касаний',
      hint: 'Отмечать каждое возвращение цены в блок. Чем больше касаний, тем слабее блок: заявки в нём съедаются.',
      type: 'boolean',
      default: true,
    },
    {
      key: 'midline',
      label: 'Пунктир по середине',
      hint: 'Середина блока: глубже неё цене заходить нежелательно, если блок держит.',
      type: 'boolean',
      default: true,
    },
    {
      key: 'labels',
      label: 'Подписи',
      hint: 'Пилюля у правого края блока: таймфрейм, сторона и сколько раз к нему возвращались.',
      type: 'boolean',
      default: true,
    },
    {
      key: 'notifyTouch',
      label: 'Оповещать: цена вернулась в блок',
      hint: 'Когда включены уведомления — сообщать о каждом возвращении цены в действующий ордерблок, с номером касания.',
      type: 'boolean',
      default: true,
    },
    {
      key: 'notifyBreak',
      label: 'Оповещать: блок пробит',
      hint: 'Когда включены уведомления — сообщать, когда свеча закрылась за ордерблоком: зона больше не держит.',
      type: 'boolean',
      default: true,
    },
    {
      key: 'bullColor',
      label: 'Цвет бычьих',
      hint: 'Каким цветом рисовать блоки перед рывком вверх — те, что держат цену снизу.',
      type: 'color',
      default: BULL,
    },
    {
      key: 'bearColor',
      label: 'Цвет медвежьих',
      hint: 'Каким цветом рисовать блоки перед рывком вниз — те, что давят сверху.',
      type: 'color',
      default: BEAR,
    },
    {
      key: 'opacity',
      label: 'Прозрачность заливки, %',
      hint: 'Больше процент — бледнее заливка, и свечи под ней видно лучше.',
      type: 'number',
      default: 50,
      min: 0,
      max: 95,
      step: 5,
    },
    TABLE_PARAM,
  ],
  outputs: [],
  timeframesOf: (p, chart) => [
    ...(boolParam(p, 'tfCurrent', true) ? [chart] : []),
    ...FRAMES.filter(
      (entry) => boolParam(p, entry.key, entry.timeframe === '4H') && timeframeMs(entry.timeframe) > timeframeMs(chart),
    ).map((entry): Timeframe => entry.timeframe),
  ],
  warmup: () => 0,
  calc: () => ({}),
  shapes: shapesOf,
  readout: readoutOf,
  legend: LEGEND,
  label: () => 'Ордерблоки',
};
